import { useTranslation } from 'react-i18next';

import { Column } from 'design-system-web';
import { useBillingPaymentSettingsForChannel } from 'lane-shared/domains/billingPayments/hooks';
import {
  Markup,
  MarkupAmountType,
  Product,
  ProductCategory,
  Tax,
  TaxAmountType,
  ChargeCode,
} from 'lane-shared/domains/billingPayments/types';
import {
  AdminFeeAmountType,
  type AdminFee,
  type AdminFeeTax,
} from 'application-types';
import { formatCurrency } from 'domains-billing-currency';
import {
  useBpAdminFeeChargeCodeEnabled,
  useBpMarkupChargeCodeEnabled,
} from 'react-hooks';

export function useGetProductsServicesColumns(
  channelId: string | undefined
): Column<Product>[] {
  const { t } = useTranslation();
  const { currency } = useBillingPaymentSettingsForChannel({ channelId });
  const isAdminFeeEnabled = useBpAdminFeeChargeCodeEnabled();
  const isMarkupChargeCodeEnabled = useBpMarkupChargeCodeEnabled();

  const renderFixedAmountCell = formatCurrency(currency);

  const columns: Column<Product>[] = [
    {
      key: 'name',
      header: t('abp.productsServices.table.productName'),
    },
    {
      key: 'productCategory',
      header: t('abp.productsServices.table.categoryName'),
      renderCell: (cell: ProductCategory) => cell.name,
    },
    {
      key: 'productType',
      header: t('abp.productsServices.table.productType'),
      renderCell: (_: undefined, row: Product) =>
        t(
          `abp.productsServices.productType.${row.productCategory.productType}`
        ),
      disableSorting: true,
    },
    {
      key: 'chargeCode',
      header: t('abp.productsServices.table.chargeCode'),
      renderCell: (cell: ChargeCode) => cell?.chargeCode || '',
    },
    {
      key: 'amount',
      header: t('abp.productsServices.table.rate'),
      renderCell: renderFixedAmountCell,
      disableSorting: true,
    },
    {
      key: 'markup',
      header: t('abp.productsServices.table.markup'),
      renderCell: (cell: Markup) =>
        cell.markupAmountType === MarkupAmountType.MarkupAmountTypePercentage
          ? `${cell.value}%`
          : renderFixedAmountCell(cell.value),
      disableSorting: true,
    },
    ...(isMarkupChargeCodeEnabled
      ? [{
        key: 'markupChargeCode',
        header: t('abp.productsServices.table.markupChargeCode'),
        renderCell: (cell: ChargeCode | null) =>
          cell?.chargeCode || '',
        disableSorting: true,
      }]
      : []),
    ...(isAdminFeeEnabled
      ? [{
        key: 'adminFee',
        header: t('abp.productsServices.table.adminFee'),
        renderCell: (cell: AdminFee | null) => {
          if (!cell) {
            return '';
          }

          return cell.adminFeeAmountType ===
            AdminFeeAmountType.AdminFeeAmountTypePercentage
            ? `${cell.value}%`
            : renderFixedAmountCell(cell.value);
        },
        disableSorting: true,
      },
      {
        key: 'adminFeeChargeCode',
        header: t('abp.productsServices.table.adminFeeChargeCode'),
        renderCell: (cell: ChargeCode | null) =>
          cell?.chargeCode || '',
        disableSorting: true,
      },
      {
        key: 'adminFeeTax',
        header: t('abp.productsServices.table.adminFeeTax'),
        renderCell: (cell: AdminFeeTax | null) => {
          if (!cell) {
            return '';
          }

          return cell.taxAmountType ===
            TaxAmountType.TaxAmountTypePercentage
            ? `${cell.rate}%`
            : renderFixedAmountCell(cell.rate);
        },
        disableSorting: true,
      }]
      : []),
    {
      key: 'tax',
      header: t('abp.productsServices.table.tax'),
      renderCell: (cell: Tax) =>
        cell.taxAmountType === TaxAmountType.TaxAmountTypePercentage
          ? `${cell.value}%`
          : renderFixedAmountCell(cell.value),
      disableSorting: true,
    },
    {
      key: 'total',
      header: t('abp.productsServices.table.total'),
      renderCell: (_: undefined, row: Product) => {
        const netPrice = row.sellingPrice?.netPrice ?? 0;

        return renderFixedAmountCell(netPrice);
      },
    },
  ];

  return columns;
}
